import React from 'react'
import { ChatThread } from './ChatThread'
import { CollapsedChatsRail } from '../queue/CollapsedChatsRail'
import { ChatLayoutProvider, useChatLayout } from '../../contexts/ChatLayoutContext'

/**
 * The chats split-view: a collapsible list column beside the open chat.
 *  • Normal:      380px list + chat
 *  • Collapsed:   56px counts rail + chat  (user pressed the collapse chevron)
 *  • Co-browsing: list hidden entirely     (ChatThread flips cobrowseActive) so
 *                 it's just the visitor's screen + the chat.
 */
const Workspace: React.FC<{ list: React.ReactNode; chatId: string }> = ({ list, chatId }) => {
  const { collapsed, cobrowseActive, toggleUser } = useChatLayout()

  // While co-browsing the list is removed entirely (single column). Otherwise a
  // slim rail (collapsed) or the full list sits beside the chat.
  const gridTemplateColumns = cobrowseActive ? '1fr' : `${collapsed ? '56px' : '380px'} 1fr`

  return (
    <div
      className="grid h-full overflow-hidden"
      style={{ gridTemplateColumns }}
    >
      {!cobrowseActive && (
        <div className="relative overflow-hidden min-h-0 h-full">
          {collapsed ? (
            <CollapsedChatsRail />
          ) : (
            <div className="border-r border-border overflow-hidden min-h-0 h-full">
              {list}
              {/* Collapse chevron — straddles the divider at top-3, mirroring the
                  account-sidebar toggle and the rail's expand chevron. */}
              <button
                onClick={toggleUser}
                title="Collapse chats list"
                aria-label="Collapse chats list"
                className="absolute top-3 right-2 z-20 flex items-center justify-center w-6 h-6 rounded-md bg-surface border border-border text-txt-secondary hover:text-brand-600 hover:bg-surface-hover shadow-sm transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
      <div className="overflow-hidden min-h-0 h-full">
        <ChatThread chatId={chatId} />
      </div>
    </div>
  )
}

export const ChatWorkspace: React.FC<{ list: React.ReactNode; chatId: string }> = (props) => (
  <ChatLayoutProvider>
    <Workspace {...props} />
  </ChatLayoutProvider>
)
